import { ReactNode, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/catalyst/button";
import { AnimatePresence, motion } from "framer-motion";
import { CheckIcon, CopyIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export function CopyToClipboard({
  text,
  children,
  title,
  className,
}: {
  text: string | null;
  children?: ReactNode;
  title?: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  if (!text) return <div className="text-gray-400">--</div>;

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard", {
      description: text.length > 30 ? `${text.substring(0, 30)}...` : text,
      duration: 2000,
    });

    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  return (
    <button
      title={title || `Copy ${children ? "text" : text} to clipboard`}
      className={cn(
        "focus:outline-hidden relative flex items-center rounded-sm text-left transition-colors focus:ring-1 focus:ring-gray-300",
        className,
      )}
      onClick={handleCopy}
      aria-label="Copy to clipboard"
    >
      {children ? children : text}

      {copied && (
        <span className="animate-fade-in-out absolute -top-6 left-1/2 -translate-x-1/2 rounded bg-gray-800 px-1.5 py-0.5 text-xs text-white">
          Copied
        </span>
      )}
    </button>
  );
}

export function CopyToClipboardButton({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Success", {
      description: `'${text}' to clipboard`,
    });

    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  return (
    <Button
      plain
      onClick={handleCopy}
      className={cn("relative size-8 p-0 sm:p-0", className)}
    >
      <AnimatePresence mode="wait">
        {copied ? (
          <motion.div
            key="check"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <CheckIcon className="h-4 w-4 text-green-500" data-slot="icon" />
          </motion.div>
        ) : (
          <motion.div
            key="copy"
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <CopyIcon className="h-4 w-4" data-slot="icon" />
          </motion.div>
        )}
      </AnimatePresence>
    </Button>
  );
}
